import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  AuthError,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { auth } from "../config/firebaseConfig";
import axios from "axios";
import "./FirebaseLoginForm.css";

interface FirebaseLoginFormProps {
  onLoginSuccess?: () => void;
}

export const FirebaseLoginForm: React.FC<FirebaseLoginFormProps> = ({
  onLoginSuccess,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"choice" | "email" | "emaillink">("choice");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isProcessingLink, setIsProcessingLink] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const googleProvider = new GoogleAuthProvider();

  // /us landing CTAs link to /login?from=us — show trial copy instead of the
  // generic login header so ad visitors see the same promise they clicked on.
  const fromUSLanding =
    new URLSearchParams(window.location.search).get("from") === "us";

  // NOTE: this component does NOT navigate after a successful login. The
  // moment exchangeTokenForJWT dispatches 'auth-token-saved', AuthContext
  // flips isAuthenticated and the /login route swaps to PostLoginRedirect
  // (AppRouter) — the single owner of the post-login destination
  // (?next → post_login_next → /codemate). Navigating from here as well
  // creates a race where both sides consume post_login_next and the loser
  // drops the user into /codemate (issue #142).

  // Complete an email-link sign-in when the user arrives via the link we
  // mailed them. This MUST live in whatever component /login renders — it
  // used to sit in MagicLinkLogin, which lost its route in the Mar 2026 login
  // consolidation, leaving mailed sign-in links dead on arrival (issue #141).
  useEffect(() => {
    const completeEmailLinkSignIn = async () => {
      if (!isSignInWithEmailLink(auth, window.location.href)) return;

      setIsProcessingLink(true);
      console.log("[Firebase] 📧 Detected email link callback");

      // Saved when the link was sent; absent if opened on another device.
      let storedEmail = window.localStorage.getItem("emailForSignIn");
      if (!storedEmail) {
        storedEmail = window.prompt("Please enter your email to confirm sign-in:");
      }
      if (!storedEmail) {
        setError("Email is required to complete sign-in.");
        setIsProcessingLink(false);
        return;
      }

      try {
        const result = await signInWithEmailLink(auth, storedEmail, window.location.href);
        const idToken = await result.user.getIdToken();
        console.log("[Firebase] ✅ Email link sign-in successful:", result.user.email);

        await exchangeTokenForJWT(idToken);
        window.localStorage.removeItem("emailForSignIn");

        onLoginSuccess?.();
        // PostLoginRedirect takes over once isAuthenticated flips; the
        // "Signing you in..." screen stays up until then.
      } catch (err: any) {
        console.error("[Firebase] ❌ Email link sign-in failed:", err);
        setError(
          err.code === "auth/invalid-action-code"
            ? "This link has expired or already been used. Please request a new one."
            : getErrorMessage(err)
        );
        setIsProcessingLink(false);
      }
    };

    completeEmailLinkSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Exchange Firebase ID token for JWT
   */
  const exchangeTokenForJWT = async (idToken: string) => {
    try {
      console.log("[Firebase] 🔄 Exchanging Firebase token for JWT...");
      const adVisitId = localStorage.getItem('ad_visit_id') || undefined;
      const refCode = new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('referral_code') || undefined;
      const response = await axios.post(
        `${apiUrl}/auth/firebase`,
        { id_token: idToken, ad_visit_id: adVisitId, referral_code: refCode },
        { timeout: 30000 }
      );

      console.log("[Firebase] ✅ Response received:", {
        hasToken: !!response.data.access_token,
        responseKeys: Object.keys(response.data)
      });

      if (response.data.access_token) {
        console.log("[Firebase] 💾 Saving token to localStorage...");
        localStorage.setItem("access_token", response.data.access_token);
        sessionStorage.setItem("firebase_token", idToken);

        // Clear last session so user starts fresh after login
        localStorage.removeItem("lastCodemateSession");
        localStorage.removeItem("ad_visit_id");

        // Track referral signup if referral code present
        if (refCode && response.data.user) {
          try {
            await axios.post(`${apiUrl}/api/referral/track-signup`, {
              referral_code: refCode,
              user_id: response.data.user.id,
              email: response.data.user.email,
            });
            localStorage.removeItem("referral_code");
            console.log("[Firebase] Referral tracked:", refCode);
          } catch (refErr) {
            console.warn("[Firebase] Referral tracking failed (non-fatal):", refErr);
          }
        }

        // Store user data in sessionStorage for AuthContext to use
        if (response.data.user) {
          sessionStorage.setItem("auth_user", JSON.stringify(response.data.user));
          sessionStorage.setItem("login_count", String(response.data.user.login_count || 0));
          console.log("[Firebase] ✅ User data saved:", response.data.user.email, "logins:", response.data.user.login_count);
        }

        // Ensure token is persisted before returning
        await new Promise(resolve => setTimeout(resolve, 100));

        // Notify AuthContext immediately — don't wait for onAuthStateChanged
        window.dispatchEvent(new CustomEvent('auth-token-saved', {
          detail: { user: response.data.user }
        }));

        console.log("[Firebase] ✅ Token verification:", {
          inLocalStorage: !!localStorage.getItem("access_token"),
          tokenLength: localStorage.getItem("access_token")?.length
        });

        return response.data;
      } else {
        console.error("[Firebase] ❌ No access_token in response:", response.data);
        throw new Error("No access_token in response from server");
      }
    } catch (error: any) {
      console.error("[Firebase] ❌ JWT exchange failed:", error);
      if (error.code === "ECONNABORTED") {
        throw new Error("Request timed out. Is the backend running?");
      }
      throw new Error(
        error.response?.data?.detail || "Failed to complete authentication"
      );
    }
  };

  /**
   * Handle email/password login
   */
  const handleEmailLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!email || !password) {
        setError("Please enter email and password");
        setIsLoading(false);
        return;
      }

      console.log("[Firebase] 🔐 Attempting email login...");
      const result = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();

      console.log("[Firebase] ✅ Email authentication successful:", email);

      // Exchange token with backend
      await exchangeTokenForJWT(idToken);

      onLoginSuccess?.();
      // Redirect is owned by PostLoginRedirect — see NOTE near the top.
    } catch (error: any) {
      console.error("[Firebase] ❌ Email login failed:", error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle email/password signup
   */
  const handleEmailSignup = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!email || !password) {
        setError("Please enter email and password");
        setIsLoading(false);
        return;
      }

      if (password.length < 6) {
        setError("Password must be at least 6 characters");
        setIsLoading(false);
        return;
      }

      console.log("[Firebase] 📝 Attempting email signup...");
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();

      console.log("[Firebase] ✅ Account created:", email);

      // Exchange token with backend
      await exchangeTokenForJWT(idToken);

      onLoginSuccess?.();
      // Redirect is owned by PostLoginRedirect — see NOTE near the top.
    } catch (error: any) {
      console.error("[Firebase] ❌ Email signup failed:", error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Google OAuth login
   */
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log("[Firebase] 🔐 Attempting Google login...");
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      console.log("[Firebase] ✅ Google authentication successful:", result.user.email);

      // Exchange token with backend
      await exchangeTokenForJWT(idToken);

      onLoginSuccess?.();
      // Redirect is owned by PostLoginRedirect — see NOTE near the top.
    } catch (error: any) {
      console.error("[Firebase] ❌ Google login failed:", error);
      if (error.code !== "auth/popup-closed-by-user") {
        const errorMessage = getErrorMessage(error);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle email link (passwordless) login
   */
  const handleEmailLinkSend = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!email) {
        setError("Please enter your email");
        setIsLoading(false);
        return;
      }

      console.log("[Firebase] 📧 Sending sign-in link to:", email);
      // Carry the pending detour (e.g. /redeem) in the continue URL so it
      // survives the link being opened in another tab, browser, or device.
      const next = localStorage.getItem("post_login_next");
      const continueUrl =
        window.location.origin +
        "/login" +
        (next && next.startsWith("/") ? `?next=${encodeURIComponent(next)}` : "");
      await sendSignInLinkToEmail(auth, email, {
        url: continueUrl,
        handleCodeInApp: true,
      });

      window.localStorage.setItem("emailForSignIn", email);
      setError(null);
      setEmail("");
      setMode("choice");
      alert("✅ Sign-in link sent! Check your email.");

      console.log("[Firebase] ✅ Sign-in link sent to:", email);
    } catch (error: any) {
      console.error("[Firebase] ❌ Email link failed:", error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Extract user-friendly error message
   */
  const getErrorMessage = (error: any): string => {
    const errorCode = error.code;

    switch (errorCode) {
      case "auth/email-already-in-use":
        return "This email is already registered. Please sign in instead.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/weak-password":
        return "Password is too weak. Use at least 6 characters.";
      case "auth/user-not-found":
        return "No account found with this email. Please sign up first.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/invalid-credential":
        return "Invalid email or password. Please try again.";
      case "auth/operation-not-allowed":
        return "This sign-in method is not enabled. Please contact support.";
      case "auth/too-many-requests":
        return "Too many login attempts. Please try again later.";
      default:
        return error.message || "Authentication failed. Please try again.";
    }
  };

  /**
   * Continue as guest
   */
  const handleContinueAsGuest = () => {
    console.log("[Firebase] 👤 Continuing as guest...");
    navigate("/codemate", { replace: true });
  };

  // Completing an email-link sign-in — show progress instead of the choice
  // screen so the user isn't asked to "log in" while already being logged in.
  if (isProcessingLink) {
    return (
      <div className="firebase-login-wrapper">
        <div className="firebase-branding-panel" style={{ display: "none" }}></div>
        <div className="firebase-form-panel full-width">
          <div className="form-container">
            <div className="form-header">
              <h2>Signing you in...</h2>
              <p>Please wait while we verify your email link</p>
            </div>
            {error ? (
              <div className="error-banner">⚠️ {error}</div>
            ) : (
              <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                <span className="spinner-mini" style={{ width: 32, height: 32 }}></span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Choice screen
  if (mode === "choice") {
    return (
      <div className="firebase-login-wrapper">
        {/* Left Panel - Branding */}
        <div className="firebase-branding-panel">
          <div className="branding-content">
            <div className="brand-header">
              <img
                src="/cubemate-logo.png"
                alt="Cubemate Logo"
                className="brand-logo"
              />
              <div className="brand-text">
                <h1 className="brand-title">Cubemate AI</h1>
                <p className="brand-subtitle">Learn, Build, and Execute — Instantly</p>
              </div>
            </div>

            <h2 className="feature-headline">
              Learn & Execute Code in 10+ Languages with AI — In Seconds
            </h2>
            <p className="feature-description">
              Stop spending hours copying code and debugging setups. Cubemate executes your code instantly across 10+ languages, powered by GPT-5.6 and Claude Opus 4.8 — so you learn faster by seeing real results, not just reading about them.
            </p>

            <ul className="features-list">
              <li>
                <span className="feature-dot"></span>
                <span>Powered by GPT-5.6 & Claude Opus 4.8</span>
              </li>
              <li>
                <span className="feature-dot"></span>
                <span>Live code execution across 10+ languages in ultra-fast cloud</span>
              </li>
              <li>
                <span className="feature-dot"></span>
                <span>Run multiple programming language tutorials at the same time in one browser window with BookMate</span>
              </li>
              <li>
                <span className="feature-dot"></span>
                <span>Build and train real machine learning models with MLmate</span>
              </li>
              <li>
                <span className="feature-dot"></span>
                <span>Go from idea to a complete AI project with Capstone</span>
              </li>
            </ul>

            <div className="branding-powered-by">
              <span>Powered by</span>
              <img src="/corestrat-logo.png" alt="CorestratAI" className="corestrat-logo-left" />
            </div>
          </div>
        </div>

        {/* Right Panel - Login */}
        <div className="firebase-form-panel">
          <div className="form-container">
            <div className="mobile-brand">
              <div className="brand-header">
                <img
                  src="/cubemate-logo.png"
                  alt="Cubemate Logo"
                  className="brand-logo-small"
                />
                <h1 className="brand-title-mobile">Cubemate Studio</h1>
              </div>
            </div>

            <div className="form-header">
              <h2>{fromUSLanding ? "Sign up free" : "Get Started for Free"}</h2>
              <p>
                {fromUSLanding
                  ? "Create your free account in 10 seconds — no card needed"
                  : "Create your account or sign in — takes 10 seconds"}
              </p>
            </div>

            {error && <div className="error-banner">⚠️ {error}</div>}

            <div className="auth-buttons-group">
              <button
                onClick={() => setMode("email")}
                disabled={isLoading}
                className="auth-btn auth-btn-primary"
              >
                {isLoading ? (
                  <>
                    <span className="spinner-mini"></span>
                    Signing in...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">✉️</span>
                    Continue with Email
                  </>
                )}
              </button>

              <button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="auth-btn auth-btn-secondary"
              >
                <svg className="btn-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <button
                onClick={() => setMode("emaillink")}
                disabled={isLoading}
                className="auth-btn auth-btn-secondary"
              >
                <span className="btn-icon">🔗</span>
                Sign in with Email Link
              </button>
            </div>

            <div className="free-credits-note">
              {fromUSLanding ? (
                <p><strong>Sign up is free</strong> — no card needed. A 7-day free Pro trial is available once you're in — cancel anytime.</p>
              ) : (
                <p>Sign up and get <strong>100 free credits</strong> — no credit card required</p>
              )}
            </div>

            <div className="form-footer">
              <p>No account yet? Just click any option above — your account is created automatically.</p>
            </div>

            <div className="powered-by-footer">
              <span className="copyright">&copy; 2026 CorestratAI Solutions</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Email screen
  if (mode === "email") {
    return (
      <div className="firebase-login-wrapper">
        <div className="firebase-branding-panel" style={{ display: "none" }}></div>
        <div className="firebase-form-panel full-width">
          <div className="form-container">
            <button
              className="back-btn"
              onClick={() => {
                setMode("choice");
                setError(null);
                setEmail("");
                setPassword("");
                setIsSignUp(false);
              }}
            >
              ← Back
            </button>

            <div className="form-header">
              <h2>{isSignUp ? "Create Account" : "Sign In"}</h2>
              <p>
                {isSignUp
                  ? "Create a new Cubemate Studio account"
                  : "Enter your email and password"}
              </p>
            </div>

            {error && <div className="error-banner">⚠️ {error}</div>}

            <div className="form-inputs">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="form-input"
                onKeyPress={(e) => e.key === "Enter" && !isSignUp && handleEmailLogin()}
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="form-input"
                onKeyPress={(e) => e.key === "Enter" && !isSignUp && handleEmailLogin()}
              />
            </div>

            <button
              onClick={isSignUp ? handleEmailSignup : handleEmailLogin}
              disabled={isLoading || !email || !password}
              className="auth-btn auth-btn-primary full-width"
            >
              {isLoading ? (
                <>
                  <span className="spinner-mini"></span>
                  {isSignUp ? "Creating account..." : "Signing in..."}
                </>
              ) : isSignUp ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </button>

            <div className="divider">
              <span>Or</span>
            </div>

            <button
              onClick={() => setIsSignUp(!isSignUp)}
              disabled={isLoading}
              className="auth-btn auth-btn-secondary full-width"
            >
              {isSignUp ? "← Sign in instead" : "Create new account →"}
            </button>

            <p className="form-note">
              {isSignUp
                ? "Already have an account? Click 'Sign in instead' to login."
                : "Don't have an account? Click 'Create new account' to sign up."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Email Link screen
  if (mode === "emaillink") {
    return (
      <div className="firebase-login-wrapper">
        <div className="firebase-branding-panel" style={{ display: "none" }}></div>
        <div className="firebase-form-panel full-width">
          <div className="form-container">
            <button
              className="back-btn"
              onClick={() => {
                setMode("choice");
                setError(null);
                setEmail("");
              }}
            >
              ← Back
            </button>

            <div className="form-header">
              <h2>Sign In with Email Link</h2>
              <p>We'll send you a sign-in link via email (no password needed)</p>
            </div>

            {error && <div className="error-banner">⚠️ {error}</div>}

            <div className="form-inputs">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="form-input"
                onKeyPress={(e) => e.key === "Enter" && handleEmailLinkSend()}
              />
            </div>

            <button
              onClick={handleEmailLinkSend}
              disabled={isLoading || !email}
              className="auth-btn auth-btn-primary full-width"
            >
              {isLoading ? (
                <>
                  <span className="spinner-mini"></span>
                  Sending link...
                </>
              ) : (
                <>
                  <span className="btn-icon">📧</span>
                  Send Sign-In Link
                </>
              )}
            </button>

            <p className="form-note">
              Check your email for a sign-in link. Click it to authenticate securely without a password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FirebaseLoginForm;
